import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { getCommissionerHistory } from 'api/karthus.client';
import { isSuccessResult } from 'utils/guards';

type HistoryRecord = {
  id: number;
  clientAccount: string;
  userName: string;
  actionType: string;
  info: string;
  replace: string;
};

type Props = {
  clientAccount: string;
};

const ACTION_BADGE: Record<string, { label: string; bg: string }> = {
  Create:      { label: 'CREADO',        bg: '#1565c0' },
  DocCharge:   { label: 'DOC CARGADO',   bg: '#2e7d32' },
  DocDelete:   { label: 'DOC ELIMINADO', bg: '#c62828' },
  SendFinance: { label: 'ENV. FINANZAS', bg: '#e65100' },
};

function ActionBadge({ actionType }: { actionType: string }) {
  const badge = ACTION_BADGE[actionType] ?? { label: actionType, bg: '#757575' };
  return (
    <Box
      component="span"
      style={{
        display: 'inline-block',
        backgroundColor: badge.bg,
        color: '#fff',
        padding: '3px 12px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {badge.label}
    </Box>
  );
}

export function HistoryComisionist({ clientAccount }: Props) {
  const [history, setHistory] = useState<HistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientAccount) return;

    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getCommissionerHistory(clientAccount);
        if (isSuccessResult(res.result)) {
          const data = res.result as any;
          // La API puede devolver un objeto único o un array
          const records: HistoryRecord[] = Array.isArray(data?.data)
            ? data.data
            : data?.data
            ? [data.data]
            : [];
          setHistory(records);
        } else {
          const errs = res.result as Error[];
          setError(errs?.[0]?.message || 'Error al cargar el historial');
        }
      } catch {
        setError('Error inesperado al obtener el historial');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [clientAccount]);

  return (
    <Card>
      <CardHeader title="Historial de Cambios" />
      <CardContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Trazabilidad de todos los cambios de estado y cargas de documentos. Solo lectura.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4} style={{ gap: '12px' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">Cargando historial...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" style={{ marginTop: '12px' }}>{error}</Alert>
        )}

        {!loading && !error && history.length === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ textAlign: 'center', padding: '32px 16px' }}
          >
            Sin registros de historial disponibles.
          </Typography>
        )}

        {!loading && !error && history.length > 0 && (
          <Box style={{ overflowX: 'auto', marginTop: '8px' }}>
            <Table style={{ minWidth: '600px' }}>
              <TableHead>
                <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '10px 12px' }}>Fecha</TableCell>
                  <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '10px 12px' }}>Usuario</TableCell>
                  <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '10px 12px' }}>Acción</TableCell>
                  <TableCell style={{ fontSize: '12px', fontWeight: 700, padding: '10px 12px' }}>Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((record) => (
                  <TableRow
                    key={record.id}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                    hover
                  >
                    <TableCell style={{ fontSize: '12px', padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      {record.info}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '10px 12px' }}>
                      {record.userName}
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '10px 12px' }}>
                      <ActionBadge actionType={record.actionType} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '10px 12px', color: '#555' }}>
                      {record.replace || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
